// src/components/LegalLandingPage.js
// Legal-research variant of the landing/login page — same layout & styles,
// different wording (kept separate from the incident LandingPage).
import React, { useState, useEffect } from 'react';
import LoginForm from './LoginForm';
import RegistrationForm from './RegistrationForm';
import './LandingPage.css';

const LegalLandingPage = () => {
    const [activeTab, setActiveTab] = useState('login');

    // Prevent body scroll when landing page is active
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'auto';
            document.documentElement.style.overflow = 'auto';
        };
    }, []);

    return (
        <div className="landing-page">
            {/* Simple Elegant Logo Bar - NO BUTTONS */}
            <div className="logo-bar-elegant">
                <div className="logo-elegant">
                    <span className="logo-icon-elegant">⚖️</span>
                    <div className="logo-text-elegant">
                        <span className="logo-main">Smart Legal Research</span>
                        <span className="logo-sub">Legal Intelligence</span>
                    </div>
                </div>
            </div>

            {/* Main Content - Two Column Layout */}
            <div className="landing-main-no-scroll">
                {/* Left Column - Marketing Content */}
                <div className="marketing-column">
                    <div className="badge-wrapper">
                        <span className="live-badge">
                            <span className="pulse-dot"></span>
                            AI-Powered Legal Research Platform
                        </span>
                    </div>

                    <h1 className="main-headline">
                        Reduce Legal Research Time by{' '}
                        <span className="highlight-gradient">70%</span>
                    </h1>

                    <p className="subheadline">
                        From 2 hours to 10 minutes — get instant case analysis,
                        relevant laws, and multi-perspective insights from defense,
                        prosecution, and judicial viewpoints.
                    </p>

                    {/* Key Metrics */}
                    <div className="metrics-row">
                        <div className="metric">
                            <div className="metric-value">70%</div>
                            <div className="metric-label">Faster Research</div>
                        </div>
                        <div className="metric-divider"></div>
                        <div className="metric">
                            <div className="metric-value">2h → 10m</div>
                            <div className="metric-label">Avg Case Review</div>
                        </div>
                        <div className="metric-divider"></div>
                        <div className="metric">
                            <div className="metric-value">28+</div>
                            <div className="metric-label">Jurisdictions</div>
                        </div>
                    </div>

                    {/* Enterprise Features */}
                    <div className="features-list">
                        <div className="feature-item">
                            <span className="feature-check">✓</span>
                            <span>Multi-LLM Powered Legal Analysis</span>
                        </div>
                        <div className="feature-item">
                            <span className="feature-check">✓</span>
                            <span>28 Jurisdictions & Legal Systems</span>
                        </div>
                        <div className="feature-item">
                            <span className="feature-check">✓</span>
                            <span>Enterprise Security (SOC2, GDPR)</span>
                        </div>
                        <div className="feature-item">
                            <span className="feature-check">✓</span>
                            <span>Defense · Prosecutor · Judge Perspectives</span>
                        </div>
                    </div>

                    {/* Trust Section */}
                    <div className="trust-section">
                        <p className="trust-text">Trusted by law firms & legal teams</p>
                        <div className="company-logos">
                            <span className="logo-placeholder">LexCorp</span>
                            <span className="logo-placeholder">Barrington</span>
                            <span className="logo-placeholder">Justicia</span>
                            <span className="logo-placeholder">CounselCo</span>
                        </div>
                    </div>
                </div>

                {/* Right Column - Auth Card */}
                <div className="auth-column">
                    <div className="auth-card-modern">
                        <div className="auth-header">
                            <h2>{activeTab === 'login' ? 'Welcome Back' : 'Create Account'}</h2>
                            <p>
                                {activeTab === 'login'
                                    ? 'Sign in to access your legal research workspace'
                                    : 'Start your 14-day free trial — no credit card required'}
                            </p>
                        </div>

                        <div className="auth-tabs-enhanced">
                            <button
                                className={`tab-enhanced ${activeTab === 'login' ? 'active' : ''}`}
                                onClick={() => setActiveTab('login')}
                            >
                                Sign In
                            </button>
                            <button
                                className={`tab-enhanced ${activeTab === 'register' ? 'active' : ''}`}
                                onClick={() => setActiveTab('register')}
                            >
                                Create Account
                            </button>
                        </div>

                        <div className="auth-body">
                            {activeTab === 'login'
                                ? <LoginForm productName="Smart Legal Research" />
                                : <RegistrationForm />}
                        </div>

                        <div className="auth-footer-note">
                            <div className="security-icons">
                                <span>🔐 SOC2 Type II</span>
                                <span>🛡️ ISO 27001</span>
                                <span>🌍 GDPR Compliant</span>
                            </div>
                            <p>Secure authentication powered by Microsoft Entra ID</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LegalLandingPage;
